import { formatLocalDateKey } from "../../utils/dateRangeUtils"
import { catalogTypeIdFromKey } from "./socialNetworkCatalog"

/** ISO date for GET …/ytd_page (month year/month or custom start_date/end_date). */
export function formatYtdPerformanceDate(date) {
  return formatLocalDateKey(date)
}

export function parseYtdPerformanceDateParts(date) {
  const iso = formatYtdPerformanceDate(date)
  if (!iso) return null
  const [year, month, day] = iso.split("-")
  if (!year || !month || !day) return null
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
  }
}

export function buildYtdPagePerformanceFetchKey(clientId, startDate, endDate, rangeMode) {
  const range = rangeMode === "custom" ? "|custom" : ""
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}${range}`
}

/** Query for GET /report/clients/{id}/ytd_page. */
export function buildClientYtdPageReportQuery({
  period,
  year,
  month,
  quarter,
  aggregationMode,
  startDate,
  endDate,
  socialNetwork,
  limit = 100,
  after,
} = {}) {
  const params = {
    aggregation_mode: aggregationMode ?? "paid_and_organic",
  }
  if (period === "custom" && startDate && endDate) {
    params.start_date = startDate
    params.end_date = endDate
  } else {
    params.period = period ?? "month"
    if (year != null) params.year = String(year)
    if ((period == null || period === "month") && month != null) params.month = String(month)
    if (period === "quarter" && quarter != null) params.quarter = String(quarter)
  }
  if (socialNetwork && socialNetwork !== "all") {
    params.social_network = socialNetwork === "instagram" ? "instagram_graph" : socialNetwork
  }
  if (limit != null) params.limit = String(limit)
  if (after) params.after = after
  return new URLSearchParams(params).toString()
}

export function unwrapYtdPageRows(data) {
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.periods)) return data.periods
  if (Array.isArray(data?.data?.periods)) return data.data.periods
  if (Array.isArray(data?.pages)) return data.pages
  if (Array.isArray(data)) return data
  return []
}

function aggregationModeToReach(mode) {
  const normalized = String(mode ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (normalized === "organic_only" || normalized === "organic") return "organic"
  if (normalized === "paid_only" || normalized === "paid") return "paid"
  return "paid_and_organic"
}

export function parseGoalMetricNumber(value) {
  if (value == null || value === "") return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Dashboard goal summary status from progress ratio (0–1). */
export function goalProgressStatusFromRatio(progressRatio) {
  if (progressRatio == null || Number.isNaN(Number(progressRatio))) return "at-risk"
  const pct = Number(progressRatio) * 100
  if (pct >= 100) return "exceeded"
  if (pct >= 70) return "on-track"
  if (pct >= 50) return "needs-attention"
  return "at-risk"
}

export function uiPeriodToGoalPerformancePeriodId(period) {
  const key = String(period ?? "")
    .trim()
    .toLowerCase()
  if (key === "quarterly" || key === "quarter") return 2
  if (key === "annual" || key === "annually" || key === "yearly" || key === "year") return 3
  return 1
}

export function goalPerformanceReachKey(reach) {
  const key = String(reach ?? "").trim().toLowerCase()
  if (key === "paid") return "paid"
  if (key === "mixed" || key === "paid_and_organic" || key === "paid & organic") {
    return "paid_and_organic"
  }
  return "organic"
}

export function goalPerformanceNetworkIdForMetric(metric, socialNetworkTypes = []) {
  const rawId = metric?.socialNetworkTypeId
  if (rawId != null && rawId !== "" && Number.isFinite(Number(rawId))) {
    return String(Number(rawId))
  }
  const fromDesc = catalogTypeIdFromKey(socialNetworkTypes, metric?.socialNetworkDescription)
  if (fromDesc != null) return String(fromDesc)
  const fromPlatform = catalogTypeIdFromKey(socialNetworkTypes, metric?.platform)
  if (fromPlatform != null) return String(fromPlatform)
  return ""
}

/** Map catalog metricName → goal-performance metrics key. */
export function ytdMetricNameForCatalogMetric(metric) {
  const raw = String(metric?.metricName ?? "").trim().toLowerCase()
  if (!raw) return ""
  const map = {
    instagram_shares: "post_shares",
    facebook_shares: "post_shares",
    instagram_saves: "post_saves",
    instagram_engagement: "new_post_engagement",
    facebook_engagement: "new_post_engagement",
    linkedin_engagement: "new_post_engagement",
    x_engagement: "new_post_engagement",
    tiktok_engagement: "new_post_engagement",
    engagement: "new_post_engagement",
    new_post_engagement: "new_post_engagement",
    instagram_engagement_rate: "new_engagement_rate",
    facebook_engagement_rate: "new_engagement_rate",
    linkedin_engagement_rate: "new_engagement_rate",
    x_engagement_rate: "new_engagement_rate",
    tiktok_engagement_rate: "new_engagement_rate",
    engagement_rate: "new_engagement_rate",
    new_engagement_rate: "new_engagement_rate",
    instagram_followers: "followers",
    facebook_followers: "followers",
    instagram_video_views: "post_video_views",
    facebook_video_views: "post_video_views",
    link_click: "link_clicks",
    instagram_impressions: "impressions",
    facebook_impressions: "impressions",
    linkedin_impressions: "impressions",
    x_impressions: "impressions",
    cross_platform_impressions: "impressions",
    instagram_comments: "post_comments",
    facebook_comments: "post_comments",
    facebook_reach: "reach",
    facebook_page_likes: "page_likes",
    impressions: "impressions",
    video_views: "post_video_views",
    purchases: "purchases",
    revenue: "revenue",
  }
  return map[raw] || raw
}

function periodIdFromApiPeriod(period) {
  const id = Number(period?.period_id ?? period?.periodId)
  if (Number.isFinite(id) && id >= 1) return id
  const label = String(period?.period ?? "").trim().toLowerCase()
  if (label.startsWith("month")) return 1
  if (label.startsWith("quarter")) return 2
  if (label.startsWith("annual") || label.startsWith("year")) return 3
  // Last 30 / custom range ytd_page rows use period: "custom" (or day).
  if (label.startsWith("custom") || label.startsWith("day") || label.startsWith("week")) {
    return 1
  }
  return null
}

/** Custom-range rows apply to whatever goal duration the user picked. */
function periodIdsFromApiPeriod(period) {
  const periodId = periodIdFromApiPeriod(period)
  if (periodId == null) return []
  const label = String(period?.period ?? "").trim().toLowerCase()
  if (label.startsWith("custom") || label.startsWith("day") || label.startsWith("week")) {
    return [1, 2, 3]
  }
  return [periodId]
}

function addNetworkKey(keys, value) {
  const key = String(value ?? "").trim()
  if (!key || keys.includes(key)) return
  keys.push(key)
}

function networkKeysForYtdRow(row, socialNetworkTypes = []) {
  const raw = row?.social_network ?? row?.socialNetwork
  const keys = []
  const catalogId = catalogTypeIdFromKey(socialNetworkTypes, raw)
  if (catalogId != null) addNetworkKey(keys, catalogId)
  const desc = String(raw ?? "").trim().toLowerCase()
  addNetworkKey(keys, desc)
  if (desc === "instagram_graph") addNetworkKey(keys, "instagram")
  if (desc === "instagram") addNetworkKey(keys, "instagram_graph")
  if (desc === "twitter") addNetworkKey(keys, "x")
  if (desc === "x") addNetworkKey(keys, "twitter")
  return keys
}

function networkKeysForGoalMetric(metric, socialNetworkTypes = []) {
  const keys = []
  addNetworkKey(keys, goalPerformanceNetworkIdForMetric(metric, socialNetworkTypes))
  const desc = String(metric?.socialNetworkDescription ?? "").trim().toLowerCase()
  addNetworkKey(keys, desc)
  if (desc === "instagram") addNetworkKey(keys, "instagram_graph")
  if (desc === "instagram_graph") addNetworkKey(keys, "instagram")
  if (desc === "x") addNetworkKey(keys, "twitter")
  if (desc === "twitter") addNetworkKey(keys, "x")
  const platform = String(metric?.platform ?? "").trim().toLowerCase()
  if (platform === "instagram") {
    addNetworkKey(keys, "instagram_graph")
    addNetworkKey(keys, "instagram")
  } else if (platform === "x") {
    addNetworkKey(keys, "twitter")
    addNetworkKey(keys, "x")
  } else if (platform) {
    addNetworkKey(keys, platform)
  }
  return keys
}

function indexKey(periodId, networkId, reach, metricName) {
  return `${periodId}|${networkId}|${reach}|${metricName}`
}

function setIndexEntry(index, periodId, networkId, reach, metricName, entry) {
  if (periodId == null || !networkId || !reach || !metricName || !entry || typeof entry !== "object") {
    return
  }
  index.set(indexKey(periodId, networkId, reach, metricName), entry)
}

function metricNameFromEntry(metricKey, entry) {
  return String(entry?.metric_name ?? entry?.metricName ?? metricKey ?? "")
    .trim()
    .toLowerCase()
}

function indexLegacyGoalPeriod(index, period) {
  const periodId = periodIdFromApiPeriod(period)
  if (periodId == null) return
  const platforms = Array.isArray(period?.platforms) ? period.platforms : []
  platforms.forEach((platform) => {
    const networkId = String(
      platform?.social_network_type_id ?? platform?.socialNetworkTypeId ?? "",
    ).trim()
    if (!networkId) return
    const metrics = platform?.metrics && typeof platform.metrics === "object" ? platform.metrics : {}
    Object.entries(metrics).forEach(([metricKey, byReach]) => {
      if (!byReach || typeof byReach !== "object") return
      ;["organic", "paid", "paid_and_organic"].forEach((reach) => {
        const entry = byReach[reach]
        if (!entry || typeof entry !== "object") return
        const metricName = metricNameFromEntry(metricKey, entry)
        if (!metricName) return
        setIndexEntry(index, periodId, networkId, reach, metricName, entry)
      })
    })
  })
}

function indexYtdPageRow(index, row, socialNetworkTypes = []) {
  const periodIds = periodIdsFromApiPeriod(row)
  if (!periodIds.length) return
  const networkIds = networkKeysForYtdRow(row, socialNetworkTypes)
  if (!networkIds.length) return
  const reach = aggregationModeToReach(row?.aggregation_mode ?? row?.aggregationMode)
  const results = row?.results
  if (!results || typeof results !== "object") return
  Object.entries(results).forEach(([metricKey, entry]) => {
    if (!entry || typeof entry !== "object") return
    const metricName = metricNameFromEntry(metricKey, entry)
    if (!metricName) return
    periodIds.forEach((periodId) => {
      networkIds.forEach((networkId) => {
        setIndexEntry(index, periodId, networkId, reach, metricName, entry)
      })
    })
  })
}

/** Index ytd_page rows (or legacy periods) by period + network + reach + metric. */
export function buildPaidOrganicPerformanceIndex(rows, socialNetworkTypes = []) {
  const index = new Map()
  const list = Array.isArray(rows) ? rows : []
  list.forEach((row) => {
    if (Array.isArray(row?.platforms)) {
      indexLegacyGoalPeriod(index, row)
      return
    }
    indexYtdPageRow(index, row, socialNetworkTypes)
  })
  return index
}

export function normalizeYtdPagePerformancePayload(data, socialNetworkTypes = []) {
  const rows = unwrapYtdPageRows(data)
  const paidOrganicIndex = buildPaidOrganicPerformanceIndex(rows, socialNetworkTypes)
  const first = rows[0]
  return {
    clientId: first?.client_id ?? first?.clientId ?? data?.client_id ?? data?.clientId ?? null,
    startDate: first?.start_date ?? first?.startDate ?? data?.start_date ?? data?.startDate ?? null,
    endDate: first?.end_date ?? first?.endDate ?? data?.end_date ?? data?.endDate ?? null,
    periods: rows,
    pages: rows,
    paidOrganicIndex: Object.fromEntries(paidOrganicIndex.entries()),
  }
}

/** API `goal_progress` is a ratio (0.66 → 66%). Always treat as ratio, never as already-percent. */
function normalizeProgressRatio(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return null
  const n = Number(value)
  if (n < 0) return 0
  return n
}

function mapPerformanceEntry(entry, extras = {}) {
  if (!entry) return null
  const goal = parseGoalMetricNumber(entry.goal)
  const current = parseGoalMetricNumber(entry.new_value ?? entry.newValue)
  const latestValue = parseGoalMetricNumber(entry.latest_value ?? entry.latestValue)
  const goalProgress = entry.goal_progress ?? entry.goalProgress
  return {
    goal,
    newValue: current,
    latestValue,
    current,
    goalProgress,
    progressRatio: normalizeProgressRatio(goalProgress),
    metricName: entry.metric_name ?? entry.metricName,
    ...extras,
  }
}

function readIndexEntry(index, periodId, networkId, reach, metricName) {
  if (!index || periodId == null || !networkId || !reach || !metricName) return null
  const key = indexKey(periodId, networkId, reach, metricName)
  if (typeof index.get === "function") return index.get(key) || null
  return index[key] || null
}

/**
 * Resolve Current (new_value), Target (goal), Progress (goal_progress)
 * for one active goal metric from GET .../ytd_page.
 */
export function lookupGoalPerformanceFromYtdIndex(index, metric, options = {}) {
  if (!index || !metric) return null
  const preferredPeriodId = uiPeriodToGoalPerformancePeriodId(options.period ?? metric?.defaultPeriod)
  const metricName = ytdMetricNameForCatalogMetric(metric)
  const networkIds = networkKeysForGoalMetric(metric, options.socialNetworkTypes)
  if (!metricName || !networkIds.length) return null

  const preferredReach = goalPerformanceReachKey(options.reach ?? metric?.source)
  const reachFallbacks = [preferredReach, "paid_and_organic", "organic", "paid"].filter(
    (value, idx, list) => value && list.indexOf(value) === idx,
  )
  const periodFallbacks = [preferredPeriodId, 1, 2, 3].filter(
    (value, idx, list) => value != null && list.indexOf(value) === idx,
  )

  for (const periodId of periodFallbacks) {
    for (const networkId of networkIds) {
      for (const reach of reachFallbacks) {
        const entry = readIndexEntry(index, periodId, networkId, reach, metricName)
        if (!entry) continue
        return mapPerformanceEntry(entry, {
          socialNetwork: networkId,
          reach,
          periodId,
        })
      }
    }
  }
  return null
}
